// The assistant conversation orchestrator. Owns the turn list, the live stream,
// staged batches, and cache invalidation. The panel is a thin view over this.
//
// Persistence: the active conversation_id lives in localStorage so the thread
// survives panel toggles and page navigation; on mount (or reopen) we load its
// history from GET /conversations/:id. "New conversation" clears it.
//
// Reconnect-safety: a stream that drops before `event:done` leaves the last user
// message retryable (`streamError` + `retryLast`) — never a frozen spinner.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { streamChat, StreamError } from './chat-client.ts';
import { confirmBatch, loadHistory, undoAction } from './api.ts';
import {
  initBatch,
  tick,
  toApplying,
  toRejected,
  toResolved,
  toFailed,
} from './batch-machine.ts';
import type {
  AssistantEvent,
  AssistantTurn,
  ChatTurn,
  BatchState,
  AppliedActionState,
  ActionResult,
  HistoryMessage,
} from './types.ts';

const LS_KEY = 'sl_assistant_conversation';

// Don't auto-resume a persisted conversation that's been idle longer than this —
// a day-old thread reappearing on reopen reads as stale. The thread isn't
// deleted (still listed under conversations); we just start fresh on next send.
const STALE_MS = 24 * 60 * 60 * 1000;

/** Latest message timestamp in a loaded history, or null when none are stamped
 *  (staleness can't be judged without timestamps — we then resume as before). */
function lastActivityAt(msgs: HistoryMessage[]): number | null {
  let max: number | null = null;
  for (const m of msgs) {
    if (m.created_at != null && (max == null || m.created_at > max)) max = m.created_at;
  }
  return max;
}

function readConversationId(): string | null {
  try {
    return localStorage.getItem(LS_KEY);
  } catch {
    return null;
  }
}
function writeConversationId(id: string | null): void {
  try {
    if (id) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
  } catch {
    /* storage disabled */
  }
}

/** entity_type → the query-key prefixes to invalidate after a confirmed batch. */
function invalidateForResults(qc: QueryClient, results: ActionResult[]): void {
  const kinds = new Set(results.filter((r) => r.ok).map((r) => r.entity_type));
  if (kinds.has('screen') || kinds.has('group')) qc.invalidateQueries({ queryKey: ['screens'] });
  if (kinds.has('group')) qc.invalidateQueries({ queryKey: ['groups'] });
  if (kinds.has('playlist')) qc.invalidateQueries({ queryKey: ['playlists'] });
  if (kinds.has('schedule')) qc.invalidateQueries({ queryKey: ['schedules'] });
  // Generated slides/snippets land in the media library (docs/09 §B2).
  if (kinds.has('media')) qc.invalidateQueries({ queryKey: ['media'] });
}

interface PendingSend {
  text: string;
  mediaIds: string[];
  attachments: { id: string; name: string }[];
}

export interface UseAssistant {
  turns: ChatTurn[];
  streaming: boolean;
  streamError: string | null;
  historyLoading: boolean;
  send: (input: PendingSend) => void;
  retryLast: () => void;
  confirm: (batchId: string, approve: boolean) => void;
  undo: (batchId: string) => void;
  newConversation: () => void;
}

export function useAssistant(): UseAssistant {
  const qc = useQueryClient();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const conversationId = useRef<string | null>(readConversationId());
  const lastSend = useRef<PendingSend | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // ---- turn/batch mutation helpers ----------------------------------------
  const patchAssistant = useCallback((turnId: string, fn: (t: AssistantTurn) => AssistantTurn) => {
    setTurns((prev) =>
      prev.map((t) => (t.id === turnId && t.role === 'assistant' ? fn(t) : t)),
    );
  }, []);

  const updateBatch = useCallback((batchId: string, fn: (b: BatchState) => BatchState) => {
    setTurns((prev) =>
      prev.map((t) =>
        t.role === 'assistant'
          ? { ...t, batches: t.batches.map((b) => (b.batch_id === batchId ? fn(b) : b)) }
          : t,
      ),
    );
  }, []);

  const updateApplied = useCallback(
    (batchId: string, fn: (a: AppliedActionState) => AppliedActionState) => {
      setTurns((prev) =>
        prev.map((t) =>
          t.role === 'assistant'
            ? { ...t, applied: t.applied.map((a) => (a.batch_id === batchId ? fn(a) : a)) }
            : t,
        ),
      );
    },
    [],
  );

  // ---- load persisted history on mount ------------------------------------
  useEffect(() => {
    const id = conversationId.current;
    if (!id) return;
    let cancelled = false;
    setHistoryLoading(true);
    loadHistory(id)
      .then((msgs) => {
        if (cancelled) return;
        // 24h staleness cutoff: if the thread's last activity is over a day old,
        // don't resume it — clear the stored id and start fresh (as if via "New
        // conversation"). The thread stays reachable in the conversation list.
        // Only applies when the history actually carries timestamps.
        const lastTs = lastActivityAt(msgs);
        if (lastTs != null && Date.now() - lastTs > STALE_MS) {
          conversationId.current = null;
          writeConversationId(null);
          return;
        }
        setTurns(
          msgs.map((m) =>
            m.role === 'user'
              ? { id: m.id, role: 'user', text: m.text, attachments: [] }
              : {
                  id: m.id,
                  role: 'assistant',
                  text: m.text,
                  tools: [],
                  batches: [],
                  applied: [],
                  streaming: false,
                },
          ),
        );
      })
      .catch(() => {
        // Stale/deleted conversation — drop it and start fresh silently.
        if (cancelled) return;
        conversationId.current = null;
        writeConversationId(null);
      })
      .finally(() => !cancelled && setHistoryLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- expiry ticking for pending batches ---------------------------------
  const hasPending = useMemo(
    () =>
      turns.some((t) => t.role === 'assistant' && t.batches.some((b) => b.status === 'pending')),
    [turns],
  );
  useEffect(() => {
    if (!hasPending) return;
    const iv = setInterval(() => {
      setTurns((prev) =>
        prev.map((t) =>
          t.role === 'assistant' ? { ...t, batches: t.batches.map((b) => tick(b)) } : t,
        ),
      );
    }, 1000);
    return () => clearInterval(iv);
  }, [hasPending]);

  // ---- streaming ----------------------------------------------------------
  const runStream = useCallback(
    (input: PendingSend, assistantTurnId: string) => {
      const controller = new AbortController();
      abortRef.current = controller;
      let sawDone = false;

      const onEvent = (ev: AssistantEvent) => {
        switch (ev.type) {
          case 'text':
            patchAssistant(assistantTurnId, (t) => ({ ...t, text: t.text + ev.data.delta }));
            break;
          case 'tool':
            patchAssistant(assistantTurnId, (t) => {
              if (ev.data.status === 'start')
                return {
                  ...t,
                  tools: [...t.tools, { name: ev.data.name, summary: ev.data.summary, done: false }],
                };
              // 'done' — resolve the last matching open tool line.
              const idx = [...t.tools].reverse().findIndex((x) => x.name === ev.data.name && !x.done);
              if (idx === -1) return t;
              const realIdx = t.tools.length - 1 - idx;
              const current = t.tools[realIdx];
              if (!current) return t;
              const tools = t.tools.slice();
              tools[realIdx] = { ...current, done: true, summary: ev.data.summary };
              return { ...t, tools };
            });
            break;
          case 'batch':
            patchAssistant(assistantTurnId, (t) => ({
              ...t,
              batches: [...t.batches, initBatch(ev.data)],
            }));
            break;
          case 'applied':
            patchAssistant(assistantTurnId, (t) => ({
              ...t,
              applied: [
                ...t.applied,
                {
                  batch_id: ev.data.batch_id,
                  summary: ev.data.summary,
                  undo_available: ev.data.undo_available,
                  status: 'applied',
                },
              ],
            }));
            // A reversible edit changed live data now — refresh the affected lists.
            qc.invalidateQueries({ queryKey: ['screens'] });
            qc.invalidateQueries({ queryKey: ['media'] });
            break;
          case 'done':
            sawDone = true;
            conversationId.current = ev.data.conversation_id;
            writeConversationId(ev.data.conversation_id);
            break;
          case 'error':
            patchAssistant(assistantTurnId, (t) => ({ ...t, error: ev.data.message }));
            break;
        }
      };

      setStreaming(true);
      setStreamError(null);
      streamChat(
        {
          conversation_id: conversationId.current ?? undefined,
          message: input.text,
          media_ids: input.mediaIds.length ? input.mediaIds : undefined,
        },
        { onEvent, signal: controller.signal },
      )
        .then(() => {
          if (!sawDone) {
            // Stream closed without a terminal event — recoverable drop.
            setStreamError('The assistant stopped responding. Retry?');
          }
        })
        .catch((err) => {
          if (err instanceof StreamError && err.code === 'aborted') return;
          const message = err instanceof Error ? err.message : 'Connection lost';
          setStreamError(message);
        })
        .finally(() => {
          patchAssistant(assistantTurnId, (t) => ({ ...t, streaming: false }));
          setStreaming(false);
          abortRef.current = null;
        });
    },
    [patchAssistant, qc],
  );

  const send = useCallback(
    (input: PendingSend) => {
      if (streaming) return;
      lastSend.current = input;
      const userTurn: ChatTurn = {
        id: crypto.randomUUID(),
        role: 'user',
        text: input.text,
        attachments: input.attachments,
      };
      const assistantTurnId = crypto.randomUUID();
      const assistantTurn: AssistantTurn = {
        id: assistantTurnId,
        role: 'assistant',
        text: '',
        tools: [],
        batches: [],
        applied: [],
        streaming: true,
      };
      setTurns((prev) => [...prev, userTurn, assistantTurn]);
      runStream(input, assistantTurnId);
    },
    [streaming, runStream],
  );

  const retryLast = useCallback(() => {
    if (streaming || !lastSend.current) return;
    // Re-run the stream for a fresh assistant turn; keep the prior user message.
    const assistantTurnId = crypto.randomUUID();
    setTurns((prev) => [
      ...prev,
      {
        id: assistantTurnId,
        role: 'assistant',
        text: '',
        tools: [],
        batches: [],
        applied: [],
        streaming: true,
      },
    ]);
    runStream(lastSend.current, assistantTurnId);
  }, [streaming, runStream]);

  const confirm = useCallback(
    (batchId: string, approve: boolean) => {
      if (!approve) {
        updateBatch(batchId, toRejected);
        void confirmBatch(batchId, false).catch(() => {
          /* rejection is local-authoritative; ignore network echo failure */
        });
        return;
      }
      updateBatch(batchId, toApplying);
      confirmBatch(batchId, true)
        .then((res) => {
          updateBatch(batchId, (b) => toResolved(b, res.results));
          invalidateForResults(qc, res.results);
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : 'Failed to apply';
          updateBatch(batchId, (b) => toFailed(b, message));
        });
    },
    [qc, updateBatch],
  );

  const undo = useCallback(
    (batchId: string) => {
      updateApplied(batchId, (a) =>
        a.status === 'applied' ? { ...a, status: 'undoing' } : a,
      );
      undoAction(batchId)
        .then((res) => {
          updateApplied(batchId, (a) => ({ ...a, status: 'undone' }));
          // Restore the affected caches to the pre-action state.
          if (res.entity_type === 'screen') qc.invalidateQueries({ queryKey: ['screens'] });
          if (res.entity_type === 'media') qc.invalidateQueries({ queryKey: ['media'] });
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : 'Could not undo';
          updateApplied(batchId, (a) => ({ ...a, status: 'undo_failed', error: message }));
        });
    },
    [qc, updateApplied],
  );

  const newConversation = useCallback(() => {
    abortRef.current?.abort();
    conversationId.current = null;
    writeConversationId(null);
    lastSend.current = null;
    setTurns([]);
    setStreamError(null);
    setStreaming(false);
  }, []);

  // Abort an in-flight stream if the whole hook unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  return {
    turns,
    streaming,
    streamError,
    historyLoading,
    send,
    retryLast,
    confirm,
    undo,
    newConversation,
  };
}
